<template>
  <AgentPendingDetails  v-if="globalType === 'agent'"
    :licensekeyParent = "licensekey"
  />
  <BinaryPendingDetails v-if="globalType === 'binary'"
    :binaryId="binaryId"
  />
  <div v-else class="p-6 dark:text-white">
    <!-- Back Button -->
    <div class="mb-6">
      <button
        @click="handleBack"
        class="mr-4 bg-blue-600 text-white px-4 py-1.5 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 text-sm font-medium flex items-center"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-4 w-4 mr-2"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L4.414 9H17a1 1 0 110 2H4.414l5.293 5.293a1 1 0 010 1.414z"
            clip-rule="evenodd"
          />
        </svg>
        Back to Configuration Preview
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center p-8">
      <div
        class="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"
      ></div>
      <span class="ml-2 text-gray-600">Loading details...</span>
    </div>

    <!-- Error State with Retry -->
    <div
      v-else-if="error"
      class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
      role="alert"
    >
      <strong class="font-bold">Error!</strong>
      <span class="block sm:inline">{{ error }}</span>
      <button
        @click="fetchDetails"
        class="mt-2 text-sm text-red-700 hover:text-red-900 underline"
      >
        Retry
      </button>
    </div>

    <!-- Details Content -->
    <div v-else-if="details">
      <div class="p-6 space-y-6 glass-card">
        <!-- Header -->
        <div>
          <h1 class="text-2xl font-bold text-gray-900 dark:text-white">
            {{ details.name }}
          </h1>
          <div v-if="details.site_name" class="mt-2 text-sm text-gray-600 dark:text-white">
            Site: {{ details.site_name }}
          </div>
          <div class="mt-2 text-sm text-gray-500 dark:text-white">ID: {{ details.id }}</div>
        </div>

        <!-- Status Information -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h2 class="text-lg font-semibold text-gray-700 dark:text-white mb-4">
              Status Information
            </h2>
            <div class="space-y-3">
              <div>
                <span class="text-gray-600 dark:text-white">Last Modified:</span>
                <span class="ml-2">{{
                  formatDateTime(details.last_modified)
                }}</span>
              </div>
              <div>
                <span class="text-gray-600 dark:text-white">Pending Changes:</span>
                <span
                  :class="
                    details.pending ? 'text-yellow-600' : 'text-green-600'
                  "
                  class="ml-2 font-medium"
                >
                  {{ details.pending ? "Yes" : "No" }}
                </span>
              </div>
              <div v-if="details.last_staterequest_status !== undefined">
                <span class="text-gray-600 dark:text-white">Last State Request:</span>
                <span
                  :class="getStatusClass(details.last_staterequest_status)"
                  class="ml-2 font-medium"
                >
                  {{ details.last_staterequest_status ? "Success" : "Failed" }}
                </span>
              </div>
            </div>
          </div>

          <!-- Last Log Entry -->
          <div
            v-if="
              details.last_logentry_message || details.last_logentry_created
            "
          >
            <h2 class="text-lg font-semibold text-gray-700 mb-4">
              Last Log Entry
            </h2>
            <div class="space-y-3">
              <div v-if="details.last_logentry_message">
                <span class="text-gray-600">Message:</span>
                <span class="ml-2 break-words">{{
                  details.last_logentry_message
                }}</span>
              </div>
              <div v-if="details.last_logentry_created">
                <span class="text-gray-600">Timestamp:</span>
                <span class="ml-2">{{
                  formatDateTime(details.last_logentry_created)
                }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Changes Section -->
        <div v-if="hasChanges" class="border-t pt-4">
          <h2 class="text-lg font-semibold text-gray-700 mb-4">
            Configuration Changes
          </h2>
          <div class="space-y-4">
            <div
              v-for="(change, field) in filteredChanges"
              :key="field"
              class="bg-gray-50 p-4 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <h3 class="font-medium text-gray-900 mb-2">
                {{ formatFieldName(field) }}
              </h3>
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <div class="text-sm text-gray-500">Original Value</div>
                  <div
                    class="mt-1 break-words"
                    :class="{ 'text-gray-400': !change.orginaljson }"
                  >
                    {{ formatValue(change.orginaljson) }}
                  </div>
                </div>
                <div>
                  <div class="text-sm text-gray-500">New Value</div>
                  <div
                    class="mt-1 break-words"
                    :class="{ 'text-gray-400': !change.pendingjson }"
                  >
                    {{ formatValue(change.pendingjson) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Pending Changes -->
        <div
          v-if="details.pending && details.differences"
          class="mt-8 bg-gray-50 dark:bg-gray-700 dark:border-gray-800 rounded-lg border border-gray-200 p-6"
        >
          <div class="flex items-center space-x-2 mb-6">
            <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Pending Changes</h2>
            <span
              class="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full"
            >
              Needs Approval
            </span>
          </div>

          <!-- New Configuration -->
          <div
            v-if="details.differences.new"
            class="bg-white dark:bg-gray-800 shadow-sm rounded-lg border-l-4 border-l-green-500 border-t border-r border-b border-gray-200 dark:border-gray-600 dark:border-l-green-500"
          >
            <div class="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-4 py-3">
              <h3 class="text-sm font-medium text-gray-900 dark:text-gray-50">
                New Configuration
              </h3>
            </div>
            <div class="p-4 space-y-3">
              <div
                v-for="(value, key) in details.differences.new"
                :key="key"
                class="flex flex-col sm:flex-row sm:items-start py-2 px-3 hover:bg-gray-50 dark:hover:bg-gray-600 dark:hover:text-gray-50 rounded"
              >
                <span
                  class="font-medium text-gray-700 dark:text-gray-50 min-w-[150px] mb-1 sm:mb-0"
                  >{{ key }}:</span
                >
                <span class="text-gray-900 dark:text-gray-50 flex-1">{{ value }}</span>
              </div>
            </div>
          </div>

          <!-- Changed Fields -->
          <div v-else class="space-y-4">
            <div
              v-for="(change, field) in details.differences"
              :key="field"
              class="bg-white shadow-sm rounded-lg border-l-4 border-l-yellow-500 border-t border-r border-b border-gray-200 dark:border-gray-600 dark:border-l-yellow-500 "
            >
              <div class="border-b border-gray-200 bg-gray-50 dark:bg-gray-700 dark:border-gray-600 px-4 py-3">
                <h3 class="text-sm font-medium text-gray-900 dark:text-gray-50 uppercase">
                  {{ field }}
                </h3>
              </div>
              <div class="p-4 dark:bg-gray-700">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <!-- Current Value -->
                  <div class="relative">
                    <div class="text-xs font-medium text-gray-500 dark:text-gray-50 mb-1">
                      Current Value
                    </div>
                    <div class="group">
                      <div
                        class="bg-red-50 dark:bg-red-600 rounded p-3 border border-red-200  dark:border-red-500 text-sm text-gray-900 dark:text-gray-50 dark:font-medium relative h-full"
                        :class="{ 'opacity-50': change.orginaljson === null }"
                      >
                        <div
                          class="absolute -left-2 top-4 w-4 h-4 bg-red-100 border border-red-300 rounded-full flex items-center justify-center"
                        >
                          <span class="text-red-600 text-xs">-</span>
                        </div>
                        {{
                          change.orginaljson === null
                            ? "Not set"
                            : change.orginaljson
                        }}
                      </div>
                    </div>
                  </div>

                  <!-- New Value -->
                  <div class="relative">
                    <div class="text-xs font-medium text-gray-500 dark:text-gray-50 mb-1">
                      New Value
                    </div>
                    <div class="group">
                      <div
                        class="bg-green-50 dark:bg-green-600 rounded p-3 border border-green-200 dark:border-green-500 text-sm text-gray-900 dark:text-gray-50 dark:font-medium relative h-full"
                      >
                        <div
                          class="absolute -left-2 top-4 w-4 h-4 bg-green-100 border border-green-300 rounded-full flex items-center justify-center"
                        >
                          <span class="text-green-600  text-xs">+</span>
                        </div>
                        {{ change.pendingjson }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-if="details.pending"
            class="mt-4"
            >
            <button 
              @click="denyApproval"
              class="btn mr-4 bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 text-sm font-medium flex items-center"
              >Deny
            </button>
          </div>
        </div>

        <!-- Related Object Changes -->
        <div v-for="(items, type) in relatedDetails" :key="type" class="mt-10">
          <h2 class="text-xl font-bold capitalize mb-4 text-gray-900 dark:text-white">{{ type === 'blackboxes' ? 'endpoint availability' : type === 'network_devices' ? 'network devices' : type }}</h2>
          <div v-if="items.length === 0" class="text-gray-500 dark:text-gray-300 italic">
            No pending related {{ type === 'blackboxes' ? 'endpoint availability' : type === 'network_devices' ? 'network devices' : type }} found.
          </div>
          <div v-for="item in items">
            <div
              :key="item.id"
              v-if="item?.pending && item?.differences"
              class="mt-4 bg-gray-50 dark:bg-gray-700 dark:border-gray-800 rounded-lg border border-gray-200 p-6"
            >
              <!-- Header -->
              <div class="flex items-center space-x-2 mb-6">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
                  Pending Changes for {{ item.name || item.job_name }}
                </h3>
                <span class="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                  Needs Approval
                </span>
              </div>

              <!-- New Configuration (if exists) -->
              <div
                v-if="item.differences?.new"
                class="bg-white dark:bg-gray-800 shadow-sm rounded-lg border-l-4 border-l-green-500 border-t border-r border-b border-gray-200 dark:border-gray-600 dark:border-l-green-500"
              >
                <div class="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-4 py-3">
                  <h3 class="text-sm font-medium text-gray-900 dark:text-gray-50">
                    New Configuration
                  </h3>
                </div>
                <div class="p-4 space-y-3">
                  <div
                    v-for="(value, key) in item.differences?.new"
                    :key="key"
                    class="flex flex-col sm:flex-row sm:items-start py-2 px-3 hover:bg-gray-50 dark:hover:bg-gray-600 dark:hover:text-gray-50 rounded"
                  >
                    <span class="font-medium text-gray-700 dark:text-gray-50 min-w-[150px] mb-1 sm:mb-0">
                      {{ key }}:
                    </span>
                    <span class="text-gray-900 dark:text-gray-50 flex-1">{{ value }}</span>
                  </div>
                </div>
              </div>

              <!-- Changed Fields -->
              <div class="space-y-4" v-else>
                <div
                  v-for="(change, field) in item.differences"
                  :key="field"
                  class="bg-white shadow-sm rounded-lg border-l-4 border-l-yellow-500 border-t border-r border-b border-gray-200 dark:border-gray-600 dark:border-l-yellow-500"
                >
                  <div class="border-b border-gray-200 bg-gray-50 dark:bg-gray-700 dark:border-gray-600 px-4 py-3">
                    <h3 class="text-sm font-medium text-gray-900 dark:text-gray-50 uppercase">
                      {{ field }}
                    </h3>
                  </div>
                  <div class="p-4 dark:bg-gray-700">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <!-- Current Value -->
                      <div class="relative">
                        <div class="text-xs font-medium text-gray-500 dark:text-gray-50 mb-1">
                          Current Value
                        </div>
                        <div class="group">
                          <div
                            class="bg-red-50 dark:bg-red-600 rounded p-3 border border-red-200 dark:border-red-500 text-sm text-gray-900 dark:text-gray-50 dark:font-medium relative h-full"
                            :class="{ 'opacity-50': change.orginaljson === null }"
                          >
                            <div
                              class="absolute -left-2 top-4 w-4 h-4 bg-red-100 border border-red-300 rounded-full flex items-center justify-center"
                            >
                              <span class="text-red-600 text-xs">-</span>
                            </div>
                            {{ change.orginaljson === null ? 'Not set' : change.orginaljson }}
                          </div>
                        </div>
                      </div>

                      <!-- New Value -->
                      <div class="relative">
                        <div class="text-xs font-medium text-gray-500 dark:text-gray-50 mb-1">
                          New Value
                        </div>
                        <div class="group">
                          <div
                            class="bg-green-50 dark:bg-green-600 rounded p-3 border border-green-200 dark:border-green-500 text-sm text-gray-900 dark:text-gray-50 dark:font-medium relative h-full"
                          >
                            <div
                              class="absolute -left-2 top-4 w-4 h-4 bg-green-100 border border-green-300 rounded-full flex items-center justify-center"
                            >
                              <span class="text-green-600 text-xs">+</span>
                            </div>
                            {{ change.pendingjson }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="mt-4">
                <button
                  @click="denyRelatedApproval(type, item.id)"
                  class="mr-4 bg-red-600 text-white px-6 py-1.5 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition duration-200 text-sm font-medium flex items-center"
                  >Deny
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { getConfigurationDetails, denyConfiguration, getRelatedConfigDetails } from "@/services/configurationService";
import AgentPendingDetails from "./AgentPendingDetails.vue";
import BinaryPendingDetails from "./BinaryPendingDetails.vue";
import { useMultiTenant } from "@/composables/useMultiTenant";

export default {
  name: "ConfigurationDetails",
  inheritAttrs: false, 
  components: {
      AgentPendingDetails,
      BinaryPendingDetails
    },
  setup() {
    const tenant = useMultiTenant();
    const route = useRoute();
    const router = useRouter();
    const details = ref(null);
    const relatedDetails = ref(null);
    const isLoading = ref(true);
    const error = ref(null);
    const licensekey = ref(null);
    const binaryId = ref(null);
    const globalType = ref("");

    // Computed properties
    const hasChanges = computed(() => {
      return (
        details.value?.changes && Object.keys(details.value.changes).length > 0
      );
    });

    const filteredChanges = computed(() => {
      if (!details.value?.changes) return {};
      return Object.entries(details.value.changes).reduce(
        (acc, [key, value]) => {
          if (
            value &&
            (value.pendingjson !== null || value.orginaljson !== null)
          ) {
            acc[key] = value;
          }
          return acc;
        },
        {}
      );
    });

    // Methods
    const handleBack = () => {
      try {
        router.back();
      } catch (err) {
        console.error("Navigation error:", err);
        router.push("/configurations"); // Fallback route
      }
    };

    const fetchDetails = async () => {
      try {
        isLoading.value = true;
        error.value = null;
        const { type: rawType, id } = route.params;

        if (!rawType || !id) {
          throw new Error("Missing required parameters");
        }
        const validation = tenant.validateSchemaForOperation();
        const schemaName = validation.schemaName;

        let type = rawType; 
        globalType.value = rawType;

        if (type === "all"){
          const response = await getRelatedConfigDetails(id, schemaName)
          relatedDetails.value = response
          type = "site";
        }

        if (globalType.value === "agent"){
          licensekey.value = id;
          return;
        }

        if (globalType.value === "binary"){
          binaryId.value = id;
          console.log(binaryId.value)
          return;
        }

        const response = await getConfigurationDetails(type, id, schemaName);
        details.value = response;

      } catch (err) {
        console.error("Error fetching configuration details:", err);
        error.value =
          err.response?.data?.message ||
          err.message ||
          "Failed to load configuration details. Please try again later.";
      } finally {
        isLoading.value = false;
      }
    };

    const denyApproval = async () => {
      try {

        const { type: rawType, id } = route.params;
        let type = rawType;
        const validation = tenant.validateSchemaForOperation();
        if (type === "all") type = "site";

        await denyConfiguration(type,id, validation.schemaName);
        handleBack();

      } catch (error) {

        console.error("Error denying configuration details:", error);
        error.value =
          error.response?.data?.message ||
          error.message ||
          "Failed to load deny. Please try again later.";
      }
    };

    const denyRelatedApproval = async (type,id) => {
      try {
        const validation = tenant.validateSchemaForOperation();
        if (type === "blackboxes") type = "blackbox";
        else if(type === "all") type = "site"
        else type = type.slice(0, -1);
         
        await denyConfiguration(type,id, validation.schemaName);
        handleBack();

      } catch (error) {

        console.error("Error denying configuration details:", error);
        error.value =
          error.response?.data?.message ||
          error.message ||
          "Failed to load deny. Please try again later.";
      }
    };

    const formatDateTime = (dateString) => {
      if (!dateString) return "N/A";
      try {
        const options = {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          timeZoneName: "short",
        };
        return new Date(dateString).toLocaleString(undefined, options);
      } catch (err) {
        console.error("Date formatting error:", err);
        return dateString || "N/A";
      }
    };

    const formatFieldName = (fieldName) => {
      if (!fieldName) return "";
      return fieldName
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    };

    const formatValue = (value) => {
      if (value === null || value === undefined) return "Not Set";
      if (Array.isArray(value)) return value.join(", ");
      if (typeof value === "object") return JSON.stringify(value, null, 2);
      if (typeof value === "boolean") return value ? "Yes" : "No";
      return String(value);
    };

    const getStatusClass = (status) => [
      "ml-2 px-2.5 py-0.5 rounded-full text-sm font-medium transition-colors",
      status ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800",
    ];

    onMounted(async () => {
      await tenant.fetchOrganizations();
      await tenant.loadInitialData(() => Promise.resolve());
      await fetchDetails();
    });

    return {
      details,
      isLoading,
      error,
      router,
      route,
      hasChanges,
      filteredChanges,
      formatDateTime,
      formatFieldName,
      formatValue,
      getStatusClass,
      handleBack,
      fetchDetails,
      denyApproval,
      relatedDetails,
      denyRelatedApproval,
      licensekey,
      globalType,
      binaryId
    
    };
  },
};
</script>
